import React, { useState, useEffect, useCallback } from 'react';
import { auditApi, AuditLogRow } from '../../api/learner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';

interface PageProps {
  isDark: boolean;
}

const PAGE_SIZE = 50;

const TARGET_LABEL: Record<string, string> = {
  user: 'Пользователь',
  order: 'Заказ',
  payment: 'Платёж',
  plan: 'Тариф',
  promo_code: 'Промокод',
  notification: 'Уведомление',
  video_lesson: 'Видеоурок',
  setting: 'Настройка',
};

export default function AuditLogPage({ isDark }: PageProps) {
  const [items, setItems] = useState<AuditLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adminIdFilter, setAdminIdFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: PAGE_SIZE };
      if (adminIdFilter.trim()) params.adminId = adminIdFilter.trim();
      if (actionFilter.trim()) params.action = actionFilter.trim();
      if (targetTypeFilter !== 'all') params.targetType = targetTypeFilter;
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      const res = await auditApi.list(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить аудит-лог');
    } finally {
      setLoading(false);
    }
  }, [page, adminIdFilter, actionFilter, targetTypeFilter, from, to]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (s: string) =>
    new Date(s).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const statusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'bg-[#EAF5EE] text-[#1F6F4A] hover:bg-[#EAF5EE]';
    if (code >= 400 && code < 500) return 'bg-[#FFFBEB] text-[#92400E] hover:bg-[#FFFBEB]';
    if (code >= 500) return 'bg-[#FFE3E3] text-[#9F1A1A] hover:bg-[#FFE3E3]';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#FBF3D9] flex items-center justify-center">
          <ScrollText className="w-6 h-6 text-[#9C7E1B]" />
        </div>
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>
            Аудит-лог админов
          </h1>
          <p className={cn('text-sm mt-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>
            All non-GET admin actions are recorded automatically
          </p>
        </div>
      </div>

      <Card className={cn(
        'p-4 rounded-2xl border-0',
        isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
      )}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Input
            placeholder="ID админа"
            value={adminIdFilter}
            onChange={(e) => { setAdminIdFilter(e.target.value); setPage(1); }}
            className="rounded-xl"
          />
          <Input
            placeholder="Действие содержит..."
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            className="rounded-xl"
          />
          <Select value={targetTypeFilter} onValueChange={(v) => { setTargetTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Тип объекта" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все объекты</SelectItem>
              {Object.entries(TARGET_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            className="rounded-xl"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            className="rounded-xl"
          />
        </div>
      </Card>

      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden',
        isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
      )}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
          </div>
        ) : items.length === 0 ? (
          <div className={cn('text-center py-16 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            No audit entries match these filters.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Когда</TableHead>
                <TableHead>Админ</TableHead>
                <TableHead>Действие</TableHead>
                <TableHead>Объект</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                  >
                    <TableCell className="text-xs whitespace-nowrap">{formatDate(row.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      {row.admin_username ?? <span className="font-mono">{row.admin_id?.slice(0, 8)}…</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.action}</TableCell>
                    <TableCell className="text-xs">
                      {row.target_type ? (
                        <span>
                          <span className="font-semibold">{TARGET_LABEL[row.target_type] ?? row.target_type}</span>
                          {row.target_id && (
                            <span className="font-mono text-slate-500 ml-1">{row.target_id.slice(0, 8)}…</span>
                          )}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('rounded-lg font-bold', statusColor(row.status_code))}>
                        {row.status_code}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{row.ip_address ?? '—'}</TableCell>
                  </TableRow>
                  {expanded === row.id && row.payload && (
                    <TableRow>
                      <TableCell colSpan={6} className={cn(
                        'px-4 py-3',
                        isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
                      )}>
                        <pre className={cn(
                          'text-xs overflow-auto max-h-60',
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        )}>
                          {JSON.stringify(row.payload, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            total={total}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
            isDark={isDark}
          />
        )}
      </Card>
    </div>
  );
}
